import React, { useState, useMemo, useRef } from 'react';
import Button from '../ui/button';
import { Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import Option from '../ui/option';
import Checkbox from '../ui/checkbox';
import TextArea from '../ui/textarea';
import Dropdown from '../ui/dropDown';
import CustomDropdown from '../ui/customDropDown';
import Input from '../ui/input';
import SubOptionModal from '../ui/subOptionModal';
import { calculateTotal } from '../../utils/addtocartUtils';
import { ChevronRight } from 'lucide-react';

// Helper function to get the correct ID (opt_id or id)
const getOptionId = (option) => option?.opt_id || option?.id;

export default function ItemCustomization({ item, onComplete, onCancel }) {
  // Helper function to transform array selections to object format
  const transformSelectionsToState = (selectionsArray) => {
    if (!selectionsArray || selectionsArray.length === 0) return {};

    // Helper to transform subModifiers array to subSelections object
    const transformSubModifiers = (subModifiers) => {
      if (!subModifiers || subModifiers.length === 0) return {};

      const subSelectionsObj = {};
      subModifiers.forEach(subMod => {
        const subGroupId = String(subMod.groupId);

        const subModData = {
          id: subMod.optionId,
          opt_id: subMod.option_id,
          name: subMod.name,
          price: subMod.price || 0,
          qty: subMod.qty || 1
        };

        // Check if groupId already exists
        if (subSelectionsObj[subGroupId]) {
          // Convert to array if not already
          if (!Array.isArray(subSelectionsObj[subGroupId])) {
            subSelectionsObj[subGroupId] = [subSelectionsObj[subGroupId]];
          }
          subSelectionsObj[subGroupId].push(subModData);
        } else {
          subSelectionsObj[subGroupId] = subModData;
        }
      });

      return subSelectionsObj;
    };

    const selectionsObj = {};
    selectionsArray.forEach(selection => {
      const groupId = selection.groupId;
      const optionGroup = item.options.find(og =>
        String(og.option_group_id) === groupId ||
        String(og.id) === groupId ||
        og.option_group_id === selection.groupId ||
        og.id === selection.groupId
      );

      // const optionGroup = item.options.find(og =>
      //   og.option_group_id === groupId || String(og.option_group_id) === String(groupId)
      // );

      if (optionGroup && optionGroup.type === 'checkbox') {
        // Multiple selections - store as array
        if (!selectionsObj[groupId]) {
          selectionsObj[groupId] = [];
        }
        const originalOption = optionGroup.option.find(o => getOptionId(o) === selection.optionId);
        selectionsObj[groupId].push({
          ...originalOption,
          id: selection.optionId,
          opt_id: selection.option_id,
          name: selection.name,
          price: selection.price,
          qty: selection.qty,
          subSelections: transformSubModifiers(selection.subModifiers) // Transform here
        });
      } else {
        console.log("coming in else");
        // Single selection (radio or single checkbox) - store as object
        const originalOption = optionGroup?.option?.find(o => getOptionId(o) === selection.optionId);
        selectionsObj[groupId] = {
          ...originalOption,
          id: selection.optionId,
          opt_id: selection.option_id,
          name: selection.name,
          price: selection.price,
          qty: selection.qty,
          subSelections: transformSubModifiers(selection.subModifiers) // Transform here
        };
      }
    });

    return selectionsObj;
  };
  // console.log('ItemCustomization received item:', item);
  const [selections, setSelections] = useState(() => {
    if (item.currentSelections && item.currentSelections.length > 0) {
      return transformSelectionsToState(item.currentSelections);
    }
    return {};
  });
  const [quantity, setQuantity] = useState(item?.currentQuantity || 1);
  const [showSubModal, setShowSubModal] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const [instructions, setInstructions] = useState(item?.currentInstructions || "");
  const [parentCustomQtyInputs, setParentCustomQtyInputs] = useState({});
  const [activeModifierIndex, setActiveModifierIndex] = useState(0);
  // auto focus instructions textarea
  const instructionsRef = useRef(null);
  const handleInstructionsFocus = () => {
    if (instructionsRef.current) {
      instructionsRef.current?.focus();
      // scroll to the top of the textarea
      setTimeout(() => {
        instructionsRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      }, 300);
    }
  };
  // TRACK INITIAL STATE FOR CHANGE DETECTION
  const initialSelections = useMemo(() => {
    if (item.currentSelections && item.currentSelections.length > 0) {
      return transformSelectionsToState(item.currentSelections);
    }
    return {};
  }, [item.currentSelections]);

  const initialQuantity = item.currentQuantity || 1;
  const initialInstructions = item.currentInstructions || "";

  const isChanged =
    JSON.stringify(selections) !== JSON.stringify(initialSelections) ||
    quantity !== initialQuantity ||
    instructions !== initialInstructions;

  const getGroupId = (group) => group?.id;
  // Handle option selection
  const handleOptionSelect = (optionGroupName, option, style) => {
    const optionId = getOptionId(option);
    setSelections(prev => {
      if (style === 'checkbox') {
        const current = Array.isArray(prev[optionGroupName]) ? prev[optionGroupName] : [];
        // console.log('Current selections for', optionGroupName, ':', current);
        //const exists = current.some(o => o.id === option.id);
        const exists = current.some(o => getOptionId(o) === optionId);
        return {
          ...prev,
          [optionGroupName]: exists
            ? //current.filter(o => o.id !== option.id)  
            current.filter(o => getOptionId(o) !== optionId)
            : [...current, { ...option, qty: 1 }]
        };
      } else if (style === 'radio') {

        const current = prev[optionGroupName];
        //  console.log("CURRENT", current);
        // if already selected, deselect on re-click
        if (current && getOptionId(current) === optionId) {
          // console.log("CURRENT", current);
          const updated = { ...prev };
          delete updated[optionGroupName];
          return updated;
        }
        return {
          ...prev,
          [optionGroupName]: option
        };
      }
      else {
        return prev;
      }
    });
    //console.log("SELECTED parent ", selections);
  };

  // NEW: Modified handler to automatically set qty to 1 on first selection
  const handleOptionClick = (optionGroup, option, e, forceOpen = false) => {
    // Prevent toggling when clicking on dropdown
    if (e?.target?.tagName === 'SELECT' || e?.target?.closest('select')) {
      return;
    }
    // Stop propagation on chevron click to prevent double click
    if (forceOpen && e) {
      e.stopPropagation();
    }

    const groupId = getGroupId(optionGroup);
    const optionId = getOptionId(option);
    setParentCustomQtyInputs(prev => {
      const copy = { ...prev };
      delete copy[`${groupId}_${optionId}`];
      return copy;
    });

    const isAlreadySelected = isOptionSelected(groupId, optionId);

    if (Array.isArray(option.sub_options) && option.sub_options.length > 0) {
      // If already selected and NOT clicking the chevron, do a silent uncheck
      if (isAlreadySelected && !forceOpen) {
        handleOptionSelect(groupId, option, optionGroup.type);
        return;
      }

      // Otherwise, open modal (either on new check or chevron click)
      const groupSelection = selections[groupId];
      const specificSelection = Array.isArray(groupSelection)
        ? groupSelection.find(o => getOptionId(o) === optionId)
        : (getOptionId(groupSelection) === optionId ? groupSelection : null);

      // For sub-options, open modal
      openSubOptionModal({
        groupId: groupId,
        parentOption: option,
        type: optionGroup.type,
        selected: specificSelection
      });

      // Set quantity to 1 if checkbox with multiple selection and not already selected
      if (!isAlreadySelected && optionGroup.type === 'checkbox' && optionGroup.allow_multiple_selection) {
        handleQtyChange(groupId, option, 1);
      }
    } else {
      // For regular options, Select/deselect Normally
      handleOptionSelect(groupId, option, optionGroup.type);

      // Set quantity to 1 if checkbox with multiple selection and newly selected
      if (!isAlreadySelected && optionGroup.type === 'checkbox' && optionGroup.allow_multiple_selection) {
        handleQtyChange(groupId, option, 1);
      }
    }
  };

  // NEW: Helper function to check if option is selected
  const isOptionSelected = (groupId, optionId) => {
    const selection = selections[groupId];
    if (!selection) return false;
    if (Array.isArray(selection)) {
      return selection.some(o => getOptionId(o) === optionId);
    }
    return getOptionId(selection) === optionId;
  };

  // NEW: Calculate total quantity selected in a group
  const getTotalQtyInGroup = (groupId) => {
    const selection = selections[groupId];
    if (!Array.isArray(selection)) return 0;
    return selection.reduce((total, opt) => total + (opt.qty || 0), 0);
  };

  // NEW: Calculate remaining quantity available in a group
  const getRemainingQty = (groupId, itemLimit, currentOptionId = null) => {
    const totalUsed = getTotalQtyInGroup(groupId);
    const selection = selections[groupId];
    const currentQty = Array.isArray(selection)
      ? (selection.find(o => o.id === currentOptionId)?.qty || 0)
      : 0;

    // Remaining = limit - total used + current option's qty (since we can change it)
    return parseInt(itemLimit, 10) - totalUsed + currentQty;
  };

  // sub option modal
  const openSubOptionModal = ({ groupId, parentOption, type, selected }) => {
    setActiveOption({ groupId, parentOption, type, selected });
    setShowSubModal(true);
  };

  const handleQtyChange = (groupId, option, newQty) => {
    const optionId = getOptionId(option);
    setSelections(prev => {
      const optionGroup = item.options.find(og => getGroupId(og) === groupId);
      const isRadio = optionGroup?.type === 'radio';

      if (isRadio) {
        return {
          ...prev,
          [groupId]: { ...option, qty: newQty }
        };
      }

      const current = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const found = current.find(o => getOptionId(o) === optionId);
      const exists = current.find(o => getOptionId(o) === optionId);

      // Get the item limit for this group
      const itemLimit = parseInt(optionGroup?.item_limit, 10);
      const isUnlimited = itemLimit === 0 || isNaN(itemLimit);
      // this is for unlimited items
      if (isUnlimited) {
        // Enable custom input when qty >= 6
        if (newQty >= 6) {
          setParentCustomQtyInputs(prevInputs => ({
            ...prevInputs,
            [`${groupId}_${optionId}`]: true,
          }));
        }

        if (found) {
          return {
            ...prev,
            [groupId]: current.map(o =>
              getOptionId(o) === optionId ? { ...o, qty: newQty } : o
            ),
          };
        }

        return {
          ...prev,
          [groupId]: [...current, { ...option, qty: newQty }],
        };
      }
      // this is for limited items- Calculate total if we update this option
      const otherOptionsTotal = current
        .filter(o => getOptionId(o) !== optionId)
        .reduce((sum, o) => sum + (o.qty || 0), 0);
      const newTotal = otherOptionsTotal + newQty;
      // If new total EXCEEDS limit, keep only this option
      if (newTotal > itemLimit) {
        return {
          ...prev,
          [groupId]: [{ ...option, qty: newQty }]
        };
      }

      // Otherwise, update normally
      if (exists) {
        return {
          ...prev,
          [groupId]: current.map(o => getOptionId(o) === optionId ? { ...o, qty: newQty } : o)
        };
      } else {
        return {
          ...prev,
          [groupId]: [...current, { ...option, qty: newQty }]
        };
      }
    });
    // console.log("SELECTED parent ", selections);
  };

  const closeSubOptionModal = () => {
    setShowSubModal(false);
    setActiveOption(null);
  };
  const handleCustomParentQtyInput = (groupId, opt, value) => {
    if (value === '') {
      setSelections(prev => {
        const existing = Array.isArray(prev[groupId]) ? prev[groupId] : [];
        const found = existing.find(o => o.id === opt.id);

        if (found) {
          return {
            ...prev,
            [groupId]: existing.map(o =>
              o.id === opt.id ? { ...o, qty: '' } : o
            )
          };
        }
        return prev;
      });
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      handleQtyChange(groupId, opt, numValue);
    }
  };
  // handle sub options
  const handleSubOptionSave = ({ parent, type, groupId, subSelections }) => {
    setSelections(prev => {
      // If it's a radio group, store as direct object
      if (type === 'radio') {
        return {
          ...prev,
          [groupId]: { ...parent, subSelections }
        };
      }

      // If it's a checkbox group, manage the array
      const current = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const parentId = getOptionId(parent);
      const exists = current.find(o => getOptionId(o) === parentId);

      if (exists) {
        // update existing parent with new subSelections
        return {
          ...prev,
          [groupId]: current.map(o =>
            getOptionId(o) === parentId ? { ...o, type, subSelections } : o
          )
        };
      }
      // append new parent option with subSelections
      return {
        ...prev,
        [groupId]: [...current, { ...parent, type, subSelections }]
      };

    });
    //console.log("FINAL SELECTIONS", JSON.stringify(selections));
    closeSubOptionModal(false);
  };

  const handleComplete = (qty = 1, instructions) => {
    const customizations = Object.entries(selections).flatMap(([groupId, options]) => {
      const selectedItems = Array.isArray(options) ? options : options ? [options] : [];

      return selectedItems.flatMap(opt => {
        const parentCustomization = {
          groupId,
          optionId: getOptionId(opt),
          option_id: opt.opt_id || opt.id,
          name: opt.name,
          price: parseFloat(opt.price) || 0,
          qty: opt.qty || 1
        };

        parentCustomization.subModifiers = opt.subSelections
          ? Object.entries(opt.subSelections).flatMap(([subGroupId, subVal]) => {
            const validSubGroup = (opt.sub_options || []).find(s =>
              String(s.id) === String(subGroupId) ||
              String(s.option_group_id) === String(subGroupId)
            );
            if (!validSubGroup) return [];

            const subArray = Array.isArray(subVal) ? subVal : [subVal];
            return subArray.map(subOpt => ({
              groupId: subGroupId,
              optionId: getOptionId(subOpt),
              option_id: subOpt.opt_id || subOpt.id,
              name: subOpt.name,
              price: parseFloat(subOpt.price) || 0,
              parent: getOptionId(opt),
              qty: subOpt.qty || 1
            }));
          })
          : [];

        return [parentCustomization];
      });
    });
    //console.log("Final customizations:", customizations);
    onComplete(item, customizations, qty, instructions, item.cartItemId);
  };


  const checkMandatoryOptions = (options, selections) => {
    return options.every(opt => {
      const selected = selections[opt.id];

      if (opt.mandatory) {
        if (!selected || (Array.isArray(selected) && selected.length === 0)) {
          return false;
        }
      }

      const selectedItems = Array.isArray(selected)
        ? selected
        : selected
          ? [selected]
          : [];

      for (const sel of selectedItems) {
        if (sel.subSelections) {
          for (const subGroup of sel.sub_options || []) {
            if (subGroup.mandatory) {
              const subSelected = sel.subSelections[subGroup.id];
              if (!subSelected || (Array.isArray(subSelected) && subSelected.length === 0)) {
                return false;
              }
            }
            if (subGroup.option && !checkMandatoryOptions(subGroup.option, selections)) {
              return false;
            }
          }
        }
      }

      return true;
    });
  };


  const allRequiredSelected = checkMandatoryOptions(item.options, selections);
  // console.log("REQUIRED", allRequiredSelected);
  return (
    <div className="h-full flex flex-col bg-white overflow-y-auto" style={{
      scrollBehavior: 'smooth',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain'
    }}>
      {/* Header */}
      <div className="relative bg-white text-white flex flex-col items-center justify-center flex-shrink-0 overflow-hidden">
        <div className="absolute top-0 right-2 z-10">
          <Button
            variant="default"
            size="icon"
            onClick={onCancel}
            className="text-black md:m-10 m-2 md:w-14 md:h-14 w-10 h-10"
          >
            <img src="/images/cross.png" alt="close" className="w-30 h-30" />
          </Button>
        </div>
        <div className="w-full flex items-start justify">
          <div className='flex rounded-xl mt-10'>
            <span >
              {item.menu_item_image && (
                <img src={item.menu_item_image} alt={item.menu_item_name} className="lg:w-[470px] lg:h-[370px] md:w-[370px] md:h-[270px] w-[170px] h-[170px] rounded-xl md:mx-8 mx-4 md:my-5 my-2 object-cover" />
              )}
              {!item.menu_item_image && (
                <div className="bg-gradient-to-br from-orange-100 to-red-100 w-[500px] sm:h-[135px] md:h-[235px] object-contain mt-4">
                  <Sparkles className="w-16 h-16 text-orange-300 " />
                </div>
              )}
            </span>
          </div>
          <div className='md:mt-20 mt-10 md:ml-12 ml-7'>
            <div className="flex flex-row lg:text-[44px] md:text-[24px] text-[14px]  text-textcolor font-outfit mt-2 font-semibold">{item.menu_item_name}</div>
            <div className="flex flex-row lg:text-[44px] md:text-[24px] text-[14px]  text-textcolor font-outfit mt-1 font-semibold">${item.menu_item_price}</div>
            <div className="flex flex-row lg:text-[30px] md:text-[20px] text-[10px]  text-secondarytext font-outfit font-normalmt-1 line-clamp-2 whitespace-pre-wrap">{item.menu_item_description}</div>
            {/* Quantity Selector */}
            <div className="flex md:mt-8 mt-4">
              <div className="flex items-center justify-center bg-white border border-boonsOrange md:border-[5px] border-[3px] rounded-full px-2 md:px-3 py-0 md:py-1">
                {/* Minus Button */}
                <Button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity === 1}
                  variant="default"
                  size="back"
                  className="text-black text-2xl md:text-4xl p-1 md:p-2 font-outfit font-medium w-3 h-3 md:w-12 md:h-12"
                >
                  -
                </Button>

                {/* Quantity Display */}
                <span className="font-outfit font-bold mx-2 md:mx-4 text-2xl md:text-4xl text-black select-none min-w-[48px] md:min-w-[64px] text-center">
                  {quantity}
                </span>

                {/* Plus Button */}
                <Button
                  onClick={() => setQuantity(q => q + 1)}
                  variant="default"
                  size="back"
                  className="text-black text-2xl md:text-4xl p-1 md:p-2 font-outfit font-medium w-3 h-3 md:w-12 md:h-12"
                >
                  +
                </Button>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* Customization Options */}

      <div className="flex-1 lg:pb-[170px] md:pb-[100px] pb-[40px]">
        {item.options && item.options.length > 0 && (
          <>
            {/* Modifiers Horizontal Scroll (only if more than one) */}
            {item.options.length > 1 && (
              <div className="bg-white px-4 pt-4">
                <div className="flex overflow-x-auto scrollbar gap-3 pb-3">
                  {item.options.map((optionGroup, idx) => {
                    const isActive = idx === activeModifierIndex;
                    return (
                      <button
                        key={optionGroup.id || idx}
                        onClick={() => setActiveModifierIndex(idx)}
                        className={`md:rounded-xl rounded-[5px] relative lg:px-6 lg:py-2 md:px-4 md:py-2 px-2 py-1 font-outfit font-medium whitespace-nowrap transition-all duration-300 ${isActive
                          ? 'bg-boonsOrange text-white'
                          : 'bg-white text-[#181c2e] border-2 border-gray-200'
                          }`}
                      >
                        <span className="relative flex items-center gap-1 md:text-[30px] text-[20px]">
                          {optionGroup.title}
                          {optionGroup.mandatory && (
                            <span className={isActive ? "text-white" : "text-red-500"}>*</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected or Single Modifier Options */}
            {(() => {
              const index = item.options.length > 1 ? activeModifierIndex : 0;
              const optionGroup = item.options[index];
              if (!optionGroup) return null;
              return (
                <div className="bg-white px-8">
                  <div className="flex items-center justify-between mb-4 mt-3">
                    <span className="md:text-[30px] text-[20px] font-outfit font-semibold text-textcolor mb-1 md:mb-5">
                      {optionGroup.title}
                      {optionGroup.mandatory && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </span>
                    <span className="md:text-[30px] text-[20px] font-outfit font-medium text-textcolor ml-5 mb-1 md:px-3 px-2 bg-[#f6f6f6] rounded-[40px]">
                      {optionGroup.mandatory ? "Required" : "Optional"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    {optionGroup.option.map((option, optIdx) => {
                      const groupId = getGroupId(optionGroup);
                      const optionId = getOptionId(option);
                      const isSelected = isOptionSelected(groupId, optionId) ||
                        (getOptionId(activeOption?.parentOption) === optionId);

                      const isDropdownEnabled = isOptionSelected(groupId, optionId);
                      const selectedOption = Array.isArray(selections[groupId])
                        ? selections[groupId].find(o => getOptionId(o) === optionId)
                        : (getOptionId(selections[groupId]) === optionId ? selections[groupId] : null);

                      // Calculate total quantity already used in this group
                      const totalUsed = getTotalQtyInGroup(groupId);
                      const currentOptionQty = selectedOption?.qty || 0;
                      const itemLimit = parseInt(optionGroup.item_limit, 10);
                      const isUnlimited = itemLimit === 0 || isNaN(itemLimit);
                      // Available = total limit - total selected
                      const availableQty = itemLimit - (totalUsed - currentOptionQty);
                      const maxQtyForThisOption = Math.max(0, Math.min(itemLimit, availableQty));

                      // Dropdown should be disabled if not selected OR no quantity available
                      const isDropdownDisabled = !isDropdownEnabled || maxQtyForThisOption <= 0;
                      // const isMaxReached = !isSelected && availableQty <= 0;
                      return (
                        <motion.button
                          key={optIdx}
                          whileTap={{ scale: 1 }}
                          onClick={(e) => handleOptionClick(optionGroup, option, e, false)}
                          className={`pb-4 rounded-xl transition-all text-left ${isSelected
                            ? 'border-gray-200'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                        >
                          <div className="flex justify-between items-center w-full gap-4">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              {/* Selection Indicator - LEFT */}
                              <div className="flex-shrink-0">
                                {optionGroup.type === 'radio' ? (
                                  <Option selected={isSelected} />
                                ) : (
                                  <Checkbox selected={isSelected} />
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="ml-10 lg:text-[30px] md:text-[25px] text-[15px] text-left font-outfit font-medium text-textcolor md:break-words">
                                  {option.name.includes("(") ? (
                                    <>
                                      {option.name.split("(")[0].trim()}
                                      <br />
                                      <span className="md:break-words">
                                        ({option.name.split("(")[1].charAt(0).toUpperCase() + option.name.split("(")[1].slice(1)}
                                      </span>
                                    </>
                                  ) : (
                                    option.name.charAt(0).toUpperCase() + option.name.slice(1)
                                  )}
                                </p>
                                {option.desc && (
                                  <p className="lg:text-[40px] md:text-[32px] text-[15px] text-left font-outfit font-normal text-secondarytext break-words">
                                    {option.desc.charAt(0).toUpperCase() + option.desc.slice(1)}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center flex-shrink-0">
                              {/* Price Container - Fixed Width for Vertical Alignment */}
                              <div className="w-[120px] flex justify-center mr-6 flex-shrink-0">
                                {option.price > 0 && (
                                  <p className="md:text-[30px] text-[15px] font-outfit md:font-medium text-textcolor whitespace-nowrap">
                                    +${option.price}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-4 justify-center min-w-[160px]">
                                {/* Chevron Container - Fixed Width */}
                                <div className="w-10 flex justify-center" onClick={(e) => handleOptionClick(optionGroup, option, e, true)}>
                                  {option.sub_options && option.sub_options.length > 0 && (
                                    <ChevronRight className="lg:w-10 lg:h-10 w-6 h-6 text-black" />
                                  )}
                                </div>
                                {/* Quantity Controls */}
                                {optionGroup.type === 'checkbox' && optionGroup.allow_multiple_selection && (
                                  <div className="min-w-[120px]">
                                    {parentCustomQtyInputs[`${groupId}_${optionId}`] ? (
                                      <input
                                        type="number"
                                        min={6}
                                        className="w-12 px-2 py-1 border border-gray-400 focus:outline-none rounded-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        disabled={!isDropdownEnabled}
                                        value={selectedOption?.qty || ''}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleCustomParentQtyInput(groupId, option, e.target.value);
                                        }}
                                      />
                                    ) : (
                                      <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onMouseDown={(e) => { e.stopPropagation(); }} className="w-20">
                                        <CustomDropdown
                                          variant="ghost"
                                          placeholder="Qty"
                                          disabled={!isDropdownEnabled}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onChange={(e) => {
                                            handleQtyChange(groupId, option, Number(e.target.value));
                                          }}
                                          value={isDropdownEnabled && selectedOption?.qty ? selectedOption.qty : ''}
                                        >
                                          {isUnlimited
                                            ? [
                                              ...[1, 2, 3, 4, 5].map(n => (
                                                <option key={n} value={n}>{n}</option>
                                              )),
                                              <option key="6" value="6">5+</option>
                                            ] : (
                                              Array.from({ length: Math.max(0, parseInt(optionGroup.item_limit, 10)) }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                  {i + 1}
                                                </option>
                                              ))
                                            )}
                                        </CustomDropdown>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                    <div className='border-b border-divider border-[2px]'></div>
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* sub modifiers */}
        <SubOptionModal
          isOpen={showSubModal}
          activeOption={activeOption}
          onClose={closeSubOptionModal}
          onSave={handleSubOptionSave}
          handleSubQtyChange={handleQtyChange}
        />
        <div className='flex justify-center p-6 md:mr-12 mt-[3px] lg:mb-[220px] md:mb-[140px] mb-[80px]' onClick={handleInstructionsFocus}>
          <TextArea className="w-full" ref={instructionsRef} value={instructions} onChange={e => { setInstructions(e.target.value) }}
            placeholder="Add special instructions..." onFocus={handleInstructionsFocus} />
        </div>

      </div>
      {/* Footer
      <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-between h-[80px]"> */}

      <div
        className={`absolute bottom-0 left-0 right-0 flex-1 bg-boonsOrange flex items-center justify-center lg:h-[180px] md:h-[100px] h-[40px] ${(!allRequiredSelected || (item.cartItemId && !isChanged)) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => {
          if (allRequiredSelected && !(item.cartItemId && !isChanged)) {
            handleComplete(quantity, instructions);
          }
        }}
      >
        <Button
          variant="orange"
          disabled={!allRequiredSelected || (item.cartItemId && !isChanged)}
          size="add"
          className="bg-boonsOrange lg:h-[180px] md:h-[100px] h-[40px] text-white font-outfit font-medium leading-[1.2] text-center whitespace-normal disabled:opacity-50 pointer-events-none"
        >
          {item.cartItemId ? `Update Cart ${calculateTotal(item.menu_item_price, selections, quantity)}` : `Add to cart ${calculateTotal(item.menu_item_price, selections, quantity)}`}
        </Button>
      </div>
      {/* </div> */}
    </div>
  );
}